import type { ComponentProps, Key } from 'react';
import type { Path } from 'react-router';
import { useProjectContext } from '@gonfalon/app-context/useProjectContext';
import { arrayMoveAfter, arrayMoveBefore } from '@gonfalon/collections/arrayMove';
import type { InternalEnvironment } from '@gonfalon/environments/types';
import invariant from 'tiny-invariant';

import { AddEnvironmentDialogTrigger, EnvironmentSelector } from '../EnvironmentSelector';
import { type SelectedEnvironment } from '../types';
import { useEnvironmentMode } from '../useEnvironmentMode';
import { useEnvironmentMenuItems } from '../useEnvironmentSettingsMenuItem';

import { useEnvironmentController } from './useEnvironmentController';

export function EnvironmentController({
  filterSearchParams,
}: {
  filterSearchParams?: ({ params }: { params: URLSearchParams }) => URLSearchParams;
}) {
  const {
    context: { projectKey },
  } = useProjectContext();

  const settingsMenuItems = useEnvironmentMenuItems();
  const hasSingleEnvironmentRoute = useEnvironmentMode() === 'single';

  const {
    addEnvironment,
    removeEnvironment,
    setEnvironments,
    replaceEnvironment,
    environments,
    selectedEnvironmentKey,
  } = useEnvironmentController({ projectKey, filterSearchParams });

  const handleSelectEnvironment = (environment: SelectedEnvironment) => {
    addEnvironment(environment);
  };

  const handleRemoveEnvironment = (key: Key) => {
    removeEnvironment(key);
  };

  const handleReplaceEnvironment = (sourceEnvironmentKey: Key, targetEnvironment: InternalEnvironment) => {
    replaceEnvironment(sourceEnvironmentKey, targetEnvironment);
  };

  const handleReorderEnvironments = (key: Key, position: 'before' | 'after', otherKeys: Set<Key>) => {
    const target = environments.find((it) => it.key === key);

    const others: SelectedEnvironment[] = [];
    for (const otherKey of otherKeys) {
      const other = environments.find((it) => it.key === otherKey);
      if (other) {
        others.push(other);
      }
    }

    invariant(target, 'Could not find environment that is being re-ordered');

    let nextList: SelectedEnvironment[];
    if (position === 'after') {
      nextList = arrayMoveAfter(environments, others, target);
    } else {
      nextList = arrayMoveBefore(environments, others, target);
    }

    let didChange = false;
    for (const [index, item] of environments.entries()) {
      if (item !== nextList[index]) {
        didChange = true;
        break;
      }
    }

    if (!didChange) {
      return;
    }

    setEnvironments(nextList, selectedEnvironmentKey ?? nextList[0].key);
  };

  const handleEnvironmentSelectionChange = (key: string) => {
    if (!hasSingleEnvironmentRoute) {
      return;
    }
    setEnvironments(environments, key);
  };

  return (
    <EnvironmentControl
      key={projectKey}
      selectionMode={hasSingleEnvironmentRoute ? 'single' : 'multiple'}
      settingsMenuItems={settingsMenuItems}
      projectKey={projectKey}
      environments={environments}
      selectedKey={selectedEnvironmentKey}
      onAddEnvironment={handleSelectEnvironment}
      onRemoveEnvironment={handleRemoveEnvironment}
      onReorderEnvironments={handleReorderEnvironments}
      onSelectionChange={handleEnvironmentSelectionChange}
      onReplaceEnvironment={handleReplaceEnvironment}
    />
  );
}

function EnvironmentControl({
  selectionMode,
  settingsMenuItems,
  projectKey,
  environments,
  selectedKey,
  onAddEnvironment,
  onRemoveEnvironment,
  onReplaceEnvironment,
  onReorderEnvironments,
  onSelectionChange,
}: {
  selectionMode: ComponentProps<typeof EnvironmentSelector>['selectionMode'];
  settingsMenuItems?: { header: string; items: Array<{ label: string; to?: Path; onAction?: () => void }> };
  projectKey: string;
  selectedKey: string | undefined;
  environments: SelectedEnvironment[];
  onAddEnvironment(environment: SelectedEnvironment): void;
  onRemoveEnvironment(key: Key): void;
  onReplaceEnvironment(sourceEnvironmentKey: Key, targetEnvironment: InternalEnvironment): void;
  onReorderEnvironments(key: Key, position: 'before' | 'after', otherKeys: Set<Key>): void;
  onSelectionChange(key: string): void;
}) {
  const handleSelectEnvironment = (environment: SelectedEnvironment) => {
    onAddEnvironment(environment);
  };

  const handleRemoveEnvironment = (key: Key) => {
    onRemoveEnvironment(key);
  };

  const handleReorderEnvironments = (key: Key, position: 'before' | 'after', otherKeys: Set<Key>) => {
    onReorderEnvironments(key, position, otherKeys);
  };

  const handleEnvironmentSelectionChange = (v: 'all' | Set<Key>) => {
    if (v === 'all' || v.size === 0) {
      return;
    }

    const nextSelectedKey = String(Array.from(v)[0]);
    onSelectionChange(nextSelectedKey);
  };

  const handleReplaceEnvironment = (sourceEnvironmentKey: Key, targetEnvironment: InternalEnvironment) => {
    onReplaceEnvironment(sourceEnvironmentKey, targetEnvironment);
  };

  return (
    <EnvironmentSelector
      projectKey={projectKey}
      selectionMode={selectionMode}
      settingsMenuItems={settingsMenuItems}
      items={environments}
      selectedKeys={selectedKey ? new Set([selectedKey]) : undefined}
      excludedIds={new Set(environments.map((it) => it._id))}
      onRemove={handleRemoveEnvironment}
      onReplace={handleReplaceEnvironment}
      onReorder={handleReorderEnvironments}
      onSelectionChange={handleEnvironmentSelectionChange}
      addButton={
        <AddEnvironmentDialogTrigger
          projectKey={projectKey}
          excludedIds={new Set(environments.map((it) => it._id))}
          onSelect={handleSelectEnvironment}
        />
      }
      dependencies={[environments]}
    />
  );
}
